import {
  ColumnMeta,
  Entity,
  FilterResultMethod,
  mapEntity,
} from '@api';
import { SkeletonLoader } from '@design';
import { ColumnDef } from '@tanstack/react-table';
import { OneDayReturnCell } from '@terminal/components/one-day-return-cell/OneDayReturnCell';
import { LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD } from '@terminal/views/screener/results/constants';
import { ScreenerCell } from '@terminal/views/screener/screener-cell/ScreenerCell';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { TFunction } from 'i18next';
import React from 'react';

import { FetchingEntity } from '../../types';
import { CompanyNameCell } from '../companies/knowledge-graph/companies-table/company-name-cell/CompanyNameCell';
import { PerformanceTableData } from './hooks/usePerformanceTable';
import styles from './performance-table/PerformanceTable.module.scss';

const TABLE_FIELDS = {
  COMPANY: 'tag',
  SECTOR: 'gics_sector',
  MARKET_CAP: 'market_cap.value',
  LIVE_ONE_DAY_RETURN: LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD,
  MOM_1W: 'mom(horiz=1w).value',
  MOM_1M: 'mom(horiz=1m).value',
  MOM_3M: 'mom(horiz=3m).value',
  MOM_6M: 'mom(horiz=6m).value',
  MOM_12M: 'mom(horiz=12m).value',
};

export const getColumns = ({
  t,
  columnsMeta,
  hash32ThemeId,
  missingEntityTags,
}: {
  t: TFunction;
  columnsMeta: ColumnMeta[];
  hash32ThemeId?: string;
  missingEntityTags: string[];
}) => {
  const themeRankFilterId = createThemeRankColumnId(hash32ThemeId);

  const oneDayReturnColumn: ColumnDef<PerformanceTableData> = {
    id: TABLE_FIELDS.LIVE_ONE_DAY_RETURN,
    header: columnsMeta.find(c => c.field === TABLE_FIELDS.LIVE_ONE_DAY_RETURN)
      ?.name,
    accessorFn: row =>
      row[TABLE_FIELDS.LIVE_ONE_DAY_RETURN as keyof PerformanceTableData],
    cell: props => {
      const c = columnsMeta.find(
        c => c.field === TABLE_FIELDS.LIVE_ONE_DAY_RETURN
      );
      const entityData = props.row.original.entity as Entity | undefined;

      if (!c) {
        return null;
      }

      return (
        <OneDayReturnCell
          asset={entityData ? mapEntity(entityData) : undefined}
        />
      );
    },
  };

  const columns: ColumnDef<PerformanceTableData>[] = [
    {
      id: themeRankFilterId,
      header: t('widget:themeOverview.tabs.performance.themeRank'),
      cell: props => {
        const { original } = props.row;
        const rank = original[
          themeRankFilterId as keyof PerformanceTableData
        ] as FilterResultMethod;

        return (
          <div className={styles.rankWrapper}>
            <TopRankIcon rank={rank?.value} />
          </div>
        );
      },
    },
    {
      id: TABLE_FIELDS.COMPANY,
      header: t('widget:themeOverview.tabs.performance.company'),
      cell: props => {
        const entity = props.row.original.entity as FetchingEntity;
        const isExcludedEntity = missingEntityTags.includes(entity.tag);

        if (!entity || entity.isFetching) {
          return (
            <SkeletonLoader
              areas={[
                {
                  styles: {
                    width: '100%',
                    height: '20px',
                    borderRadius: '8px',
                  },
                },
              ]}
            />
          );
        }

        return (
          <CompanyNameCell
            key={entity.id}
            entity={entity}
            showAlertIcon={isExcludedEntity}
          />
        );
      },
    },
    {
      id: TABLE_FIELDS.SECTOR,
      header: t('widget:themeOverview.tabs.performance.sector'),
      cell: props => {
        const entity = props.row.original.entity as FetchingEntity;

        if (entity.isFetching) {
          return (
            <SkeletonLoader
              areas={[
                {
                  styles: {
                    width: '100%',
                    height: '20px',
                    borderRadius: '8px',
                  },
                },
              ]}
            />
          );
        }

        if (!entity?.gics) {
          return null;
        }

        const sectorName = t([
          `widget:assetOverview.companyProfile.sectorNames.${entity?.gics.sector.code}`,
          'widget:assetOverview.companyProfile.sectorNames.notFound',
        ]);

        return <span className={styles.sectorName}>{sectorName}</span>;
      },
    },
    {
      id: TABLE_FIELDS.MARKET_CAP,
      header: columnsMeta.find(c => c.field === TABLE_FIELDS.MARKET_CAP)?.name,
      accessorFn: row =>
        row[TABLE_FIELDS.MARKET_CAP as keyof PerformanceTableData],
      cell: ({ getValue }) => {
        const c = columnsMeta.find(c => c.field === TABLE_FIELDS.MARKET_CAP);
        const value = getValue() as FilterResultMethod;

        if (!c) {
          return null;
        }

        return <ScreenerCell value={value} columnMeta={c} />;
      },
    },
    oneDayReturnColumn,
    {
      id: TABLE_FIELDS.MOM_1W,
      header: columnsMeta.find(c => c.field === TABLE_FIELDS.MOM_1W)?.name,
      accessorFn: row => row[TABLE_FIELDS.MOM_1W as keyof PerformanceTableData],
      cell: ({ getValue }) => {
        const c = columnsMeta.find(c => c.field === TABLE_FIELDS.MOM_1W);
        const value = getValue() as FilterResultMethod;

        if (!c) {
          return null;
        }

        return <ScreenerCell value={value} columnMeta={c} />;
      },
    },
    {
      id: TABLE_FIELDS.MOM_1M,
      header: columnsMeta.find(c => c.field === TABLE_FIELDS.MOM_1M)?.name,
      accessorFn: row => row[TABLE_FIELDS.MOM_1M as keyof PerformanceTableData],
      cell: ({ getValue }) => {
        const c = columnsMeta.find(c => c.field === TABLE_FIELDS.MOM_1M);
        const value = getValue() as FilterResultMethod;

        if (!c) {
          return null;
        }

        return <ScreenerCell value={value} columnMeta={c} />;
      },
    },
    {
      id: TABLE_FIELDS.MOM_3M,
      header: columnsMeta.find(c => c.field === TABLE_FIELDS.MOM_3M)?.name,
      accessorFn: row => row[TABLE_FIELDS.MOM_3M as keyof PerformanceTableData],
      cell: ({ getValue }) => {
        const c = columnsMeta.find(c => c.field === TABLE_FIELDS.MOM_3M);
        const value = getValue() as FilterResultMethod;

        if (!c) {
          return null;
        }

        return <ScreenerCell value={value} columnMeta={c} />;
      },
    },
    {
      id: TABLE_FIELDS.MOM_6M,
      header: columnsMeta.find(c => c.field === TABLE_FIELDS.MOM_6M)?.name,
      accessorFn: row => row[TABLE_FIELDS.MOM_6M as keyof PerformanceTableData],
      cell: ({ getValue }) => {
        const c = columnsMeta.find(c => c.field === TABLE_FIELDS.MOM_6M);
        const value = getValue() as FilterResultMethod;

        if (!c) {
          return null;
        }

        return <ScreenerCell value={value} columnMeta={c} />;
      },
    },
    {
      id: TABLE_FIELDS.MOM_12M,
      header: columnsMeta.find(c => c.field === TABLE_FIELDS.MOM_12M)?.name,
      accessorFn: row =>
        row[TABLE_FIELDS.MOM_12M as keyof PerformanceTableData],
      cell: ({ getValue }) => {
        const c = columnsMeta.find(c => c.field === TABLE_FIELDS.MOM_12M);
        const value = getValue() as FilterResultMethod;

        if (!c) {
          return null;
        }

        return <ScreenerCell value={value} columnMeta={c} />;
      },
    },
  ];

  return columns;
};

export const getAlignment = (index: number) => {
  return index > 2 ? 'end' : 'start';
};

export const createThemeRankColumnId = (hash32ThemeId?: string) => {
  return `theme_exposure.${hash32ThemeId}.theme.rank`;
};
